<template>
  <div id="essay-writing">
    <b-container fluid class="background">
      <b-row class="pt-4 pb-2 title top-nav-row responsive-padding">
        <b-col class="px-0">
          <!-- <a class="strip top-back-link" @click="handleBack()">&larr; Back to Edit Essay</a> -->
        </b-col>
        <b-col cols="auto" class="px-0 feedback-modules-col">
          <a class="strip top-back-link" @click="goToSelectModule">Go to Modules &rarr;</a>
        </b-col>
      </b-row>
      <b-row class="responsive-padding breadcrumb-row">
        <breadcrumb :items="items"></breadcrumb>
      </b-row>
      <section class="page-hero responsive-padding">
        <h1 class="text-center prompt-title page-title">Feedback on Your Essay</h1>
        <p class="page-subtitle">Review each section below and use the guidance to improve your next draft.</p>
      </section>
      <b-row
        class="px-1 px-sm-4 px-md-4 px-lg-4 white-container responsive-padding mx-0 mx-sm-4 mx-md-4 mx-lg-4"
      >
        <div class="feedback-summary-grid">
            <feedback-attempts-banner
              :remaining-attempts="remainingFeedbackAttempts"
              :is-authenticated="isAuthenticated"
              :is-pro-user="isProUser"
            />
        </div>

        <essayhead :prompt="exersize.prompt" :score="score"></essayhead>
        <sectionfeedback
          class="mt-4"
          v-for="section in stream"
          :key="section.title"
          :title="section.title"
          :points="section.points"
          :content="section.content"
          :title_2="section.title_2"
          :points_2="section.points_2"
          :content_2="section.content_2"
        ></sectionfeedback>
      </b-row>

      <transition name="fade">
        <div
          v-if="showAttemptConfirmation"
          class="attempt-confirm-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div class="attempt-confirm-card">
            <p class="feedback-card-label">Use 1 feedback attempt?</p>
            <h2 class="attempt-confirm-title">Request updated AI feedback</h2>
            <p class="attempt-confirm-copy">
              {{ attemptConfirmationCopy }}
            </p>
            <p class="attempt-confirm-meta">
              {{ remainingAttemptsText }}
            </p>
            <div class="attempt-confirm-actions">
              <button
                class="feedback-action-button feedback-action-button--secondary"
                :disabled="isRefreshingFeedback"
                @click="closeAttemptConfirmation()"
              >
                Cancel
              </button>
              <button
                class="feedback-action-button feedback-action-button--primary"
                :disabled="isRefreshingFeedback"
                @click="requestUpdatedFeedback()"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </transition>

      <feedback-generation-modal
        :visible="isRefreshingFeedback"
        :loading-step-message="loadingStepMessage"
        :show-long-wait-message="showLongWaitMessage"
        title="Generating updated feedback."
        subtitle-text="Reviewing your revised draft"
        :meta-text="feedbackLoadingMetaText"
        safe-text="Your draft is safe while updated feedback is being generated."
        image-alt="Generating updated feedback"
      />
    </b-container>
  </div>
</template>

<script>
import { onMounted, ref, computed } from "vue";
import { useRoute } from "vue-router";
import { goToChoosePrompt, goToLEQEssay, goToSelectModule } from "@/router/navigation";
import feedbackGenerationModal from "../../components/writing_feature/FeedbackGenerationModal.vue";
import {
  getFeedback,
  isAuthRequiredError,
  isFeedbackLimitError
} from "@/services/getFeedback";
import { useSubscriptionAccess } from "@/composables/useSubscriptionAccess";
import { trackEvent } from "@/services/analyticsService";
import breadcrumb from "../../components/writing_feature/BreadCrumb.vue";
import essayhead from "../../components/writing_feature/LEQEssay/EssayHead.vue";
import feedbackAttemptsBanner from "../../components/writing_feature/FeedbackAttemptsBanner.vue";
import storeWriting from "../../store/writing";
import sectionfeedback from "../../components/writing_feature/SectionFeedback.vue";
export default {
  components: {
    breadcrumb,
    essayhead,
    sectionfeedback,
    feedbackGenerationModal,
    feedbackAttemptsBanner
  },
  setup() {
    //ROUTE && FEEDBACK OBJECT ID
    const route = useRoute();
    const store = storeWriting;
    const feedbackVersion = ref(null);
    const feedbackId = computed(() => route.params.id);
    const {
      isAuthenticated,
      isProUser,
      normalizedProfile,
      canRequestFeedback,
      refreshSubscriptionProfile
    } = useSubscriptionAccess();

    //REFS && CONSTANTS
    const variable = ref(null);
    const prompt_id = ref(null);
    const score = ref(null);
    const stream = ref([]);
    const feedbackPromptText = ref("");
    const activeFeedback = ref(null);
    const showAttemptConfirmation = ref(false);
    const updateFeedbackError = ref("");
    const isRefreshingFeedback = ref(false);
    const loadingStepIndex = ref(0);
    const showLongWaitMessage = ref(false);

    const loadingStepMessages = [
      "Checking your contextualization...",
      "Reviewing your thesis and evidence...",
      "Evaluating your analysis...",
      "Putting together revision feedback..."
    ];
    const loadingStepMessage = computed(
      () => loadingStepMessages[loadingStepIndex.value]
    );

    let loadingStepIntervalId = null;
    let longWaitTimeoutId = null;

    const stopSubmittingUi = () => {
      isRefreshingFeedback.value = false;
      showLongWaitMessage.value = false;

      if (loadingStepIntervalId) {
        clearInterval(loadingStepIntervalId);
        loadingStepIntervalId = null;
      }

      if (longWaitTimeoutId) {
        clearTimeout(longWaitTimeoutId);
        longWaitTimeoutId = null;
      }
    };

    const startSubmittingUi = () => {
      stopSubmittingUi();
      isRefreshingFeedback.value = true;
      loadingStepIndex.value = 0;

      loadingStepIntervalId = setInterval(() => {
        loadingStepIndex.value =
          (loadingStepIndex.value + 1) % loadingStepMessages.length;
      }, 3500);

      longWaitTimeoutId = setTimeout(() => {
        showLongWaitMessage.value = true;
      }, 25000);
    };

    onMounted(async () => {
      window.scrollTo({ top: 0, behavior: "smooth" });

      trackEvent("feedback_viewed", {
        feedback_id: feedbackId.value,
        is_authenticated: isAuthenticated.value
      });

      if (isAuthenticated.value) {
        try {
          await refreshSubscriptionProfile();
        } catch (error) {
          console.warn("Could not refresh subscription profile on feedback page:", error);
        }
      }
    });

    const exersize = computed(() => {
      const promptFromStore = store.state.prompts.find(p => p.id === prompt_id.value);
      if (feedbackPromptText.value) {
        return {
          prompt: promptFromStore
            ? { ...promptFromStore, prompt: feedbackPromptText.value }
            : { prompt: feedbackPromptText.value }
        };
      }

      return { prompt: promptFromStore };
    });
    const items = [
      {
        text: "Essay Writing: LEQ"
      },
      {
        text: "Choose a Prompt"
      },
      {
        text: "LEQ Essay"
      },
      {
        text: "Feedback",
        active: "yes!"
      }
    ];
    const buttonprops = ref({
      content: "Get Started",
      route: "SelectModule",
      disabled: false
    });

    const freeFeedbackLimit = computed(
      () => normalizedProfile.value.freeFeedbackLimit || 2
    );

    const usedFeedbackAttempts = computed(() =>
      Math.min(normalizedProfile.value.feedbackCount || 0, freeFeedbackLimit.value)
    );

    const remainingFeedbackAttempts = computed(() => {
      if (isProUser.value) {
        return null;
      }
      return normalizedProfile.value.remainingFreeFeedback;
    });

    const remainingAttemptsText = computed(() => {
      if (isProUser.value) {
        return "Unlimited feedback attempts available.";
      }

      if ((remainingFeedbackAttempts.value || 0) > 1) {
        return `${remainingFeedbackAttempts.value} feedback attempts remaining.`;
      }

      if ((remainingFeedbackAttempts.value || 0) === 1) {
        return "1 feedback attempt remaining.";
      }

      return "No feedback attempts remaining.";
    });

    const attemptStatusTone = computed(() => {
      if (isProUser.value) return "pro";
      if ((remainingFeedbackAttempts.value || 0) > 0) return "active";
      return "locked";
    });

    const revisionStageLabel = computed(() => {
      if (usedFeedbackAttempts.value <= 1) {
        return "Draft 1 -> Feedback 1";
      }

      if (usedFeedbackAttempts.value === 2) {
        return "Draft 2 -> Feedback 2";
      }

      return `Feedback round ${usedFeedbackAttempts.value}`;
    });

    const revisionCoachCopy = computed(() => {
      if (usedFeedbackAttempts.value <= 1) {
        return "Use this first round as a diagnosis. Revise your draft freely, then spend the second attempt when you want a final revision check.";
      }

      return "Treat this second round as a revision check. Compare it against the earlier feedback and tighten the final details before moving on.";
    });

    const updatedFeedbackDisabled = computed(() => {
      if (isRefreshingFeedback.value) return true;
      if (!activeFeedback.value) return true;
      if (!isAuthenticated.value) return true;
      if (isProUser.value) return false;
      return (remainingFeedbackAttempts.value || 0) <= 0 || !canRequestFeedback.value;
    });

    const updatedFeedbackHint = computed(() => {
      if (isRefreshingFeedback.value) {
        return "Generating updated feedback...";
      }

      if (!isAuthenticated.value) {
        return "Sign in to request AI feedback.";
      }

      if (isProUser.value) {
        return "Unlimited feedback with Pro.";
      }

      if ((remainingFeedbackAttempts.value || 0) > 0) {
        return "Uses 1 attempt";
      }

      return "No feedback attempts remaining";
    });

    const updatedFeedbackButtonLabel = computed(() =>
      isRefreshingFeedback.value ? "Generating Updated Feedback..." : "Get Updated Feedback"
    );

    const attemptConfirmationCopy = computed(() => {
      if (isProUser.value) {
        return "You are requesting another round of AI feedback. Make sure you've revised your essay before continuing.";
      }

      return "This will use 1 of your remaining feedback attempts. Make sure you've revised your essay before continuing.";
    });

    const feedbackLoadingMetaText = computed(() => {
      if (isProUser.value) {
        return "Unlimited feedback is included with your plan.";
      }

      return "This uses one feedback attempt from your free plan.";
    });


    const sectionDisplayTitleMap = {
      contextualization: "Contextualization",
      thesis: "Thesis Statement",
      conclusion: "Conclusion"
    };

    const formatSectionTitle = key => {
      if (!key || typeof key !== "string") {
        return "Section";
      }

      return key
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([a-zA-Z])(\d+)/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .trim()
        .replace(/\b\w/g, char => char.toUpperCase());
    };

    const getPairNumber = (key, prefix) => {
      if (typeof key !== "string" || !key.startsWith(prefix)) {
        return null;
      }
      const suffix = key.slice(prefix.length);
      const value = Number(suffix);
      return Number.isInteger(value) && value > 0 ? value : null;
    };

    const getSectionRank = key => {
      if (key === "contextualization") return 0;
      if (key === "thesis") return 1;

      const evidencePair = getPairNumber(key, "evidence");
      if (evidencePair !== null) return 100 + evidencePair * 2;

      const analysisPair = getPairNumber(key, "analysis");
      if (analysisPair !== null) return 101 + analysisPair * 2;

      if (key === "conclusion") return 100000;
      return 1000;
    };

    const orderSectionKeys = sections => {
      const keys = Object.keys(sections || {});
      return keys.sort((a, b) => {
        const aRank = getSectionRank(a);
        const bRank = getSectionRank(b);

        if (aRank !== bRank) {
          return aRank - bRank;
        }

        return a.localeCompare(b);
      });
    };

    const toSectionContent = (section, fallbackUserInput = "") => ({
      userInput:
        section && typeof section.whatWasWritten === "string" && section.whatWasWritten.trim().length
          ? section.whatWasWritten
          : fallbackUserInput,
      good:
        section && typeof section.whatWasGood === "string"
          ? section.whatWasGood
          : "",
      toImprove:
        section && typeof section.whatToImprove === "string"
          ? section.whatToImprove
          : ""
    });

    const toPoints = section => {
      const sectionScore = Number(section && section.score ? section.score : 0);
      const sectionMaxScore = Number(section && section.maxScore ? section.maxScore : 0);
      if (sectionMaxScore === 0) {
        return "Optional";
      }
      return `${sectionScore}/${sectionMaxScore}`;
    };

    const getLocalSectionFallback = (feedbackObj, key) => {
      const content = feedbackObj && feedbackObj.content ? feedbackObj.content : null;
      if (!content) {
        return "";
      }

      if (key === "contextualization") {
        return content.contextualization?.userInput || "";
      }
      if (key === "thesis") {
        return content.thesis?.userInput || "";
      }
      if (key === "conclusion") {
        return content.conclusion?.userInput || "";
      }

      const evidenceIndex = getPairNumber(key, "evidence");
      if (evidenceIndex !== null) {
        const entry = content.evidence?.[evidenceIndex - 1];
        return entry?.evidence?.userInput || "";
      }

      const analysisIndex = getPairNumber(key, "analysis");
      if (analysisIndex !== null) {
        const entry = content.evidence?.[analysisIndex - 1];
        return entry?.analysis?.userInput || "";
      }

      return "";
    };

    const resolveFeedback = () => {
      const feedbackByVersion = store.getters.findFeedback({
        id: feedbackId.value,
        version: feedbackVersion.value
      });

      if (feedbackByVersion !== 202) {
        return feedbackByVersion;
      }

      return store.state.feedback.find(
        item => item.id && item.id.toString() === feedbackId.value.toString()
      ) || 202;
    };

    const buildFeedbackStream = feedback => {
      const backendFeedback = feedback && feedback.backendFeedback ? feedback.backendFeedback : null;
      const sections = backendFeedback && backendFeedback.sections ? backendFeedback.sections : null;

      if (!(backendFeedback && sections && typeof sections === "object")) {
        score.value = feedback.displayScore ?? feedback.score ?? null;
        return [];
      }

      score.value = backendFeedback.displayScore ?? feedback.displayScore ?? feedback.score ?? null;

      const orderedKeys = orderSectionKeys(sections);
      const availableKeys = new Set(orderedKeys);
      const nextStream = [];

      const pushSingleSection = key => {
        if (!availableKeys.has(key)) {
          return;
        }

        const section = sections[key] || {};
        nextStream.push({
          title: sectionDisplayTitleMap[key] || formatSectionTitle(key),
          points: toPoints(section),
          content: toSectionContent(section, getLocalSectionFallback(feedback, key))
        });
        availableKeys.delete(key);
      };

      pushSingleSection("contextualization");
      pushSingleSection("thesis");

      const pairIndexes = new Set();
      orderedKeys.forEach(key => {
        const evidencePair = getPairNumber(key, "evidence");
        if (evidencePair !== null) {
          pairIndexes.add(evidencePair);
        }
        const analysisPair = getPairNumber(key, "analysis");
        if (analysisPair !== null) {
          pairIndexes.add(analysisPair);
        }
      });

      Array.from(pairIndexes)
        .sort((a, b) => a - b)
        .forEach(pairIndex => {
          const evidenceKey = `evidence${pairIndex}`;
          const analysisKey = `analysis${pairIndex}`;
          if (!availableKeys.has(evidenceKey) && !availableKeys.has(analysisKey)) {
            return;
          }

          const evidenceSection = sections[evidenceKey] || {};
          const analysisSection = sections[analysisKey] || {};

          nextStream.push({
            title: `Evidence #${pairIndex}`,
            points: toPoints(evidenceSection),
            content: toSectionContent(
              evidenceSection,
              getLocalSectionFallback(feedback, evidenceKey)
            ),
            title_2: "Analysis",
            points_2: toPoints(analysisSection),
            content_2: toSectionContent(
              analysisSection,
              getLocalSectionFallback(feedback, analysisKey)
            )
          });

          availableKeys.delete(evidenceKey);
          availableKeys.delete(analysisKey);
        });

      if (availableKeys.has("conclusion")) {
        const conclusion = sections.conclusion || {};
        nextStream.push({
          title: sectionDisplayTitleMap.conclusion,
          points: toPoints(conclusion),
          content: toSectionContent(
            conclusion,
            getLocalSectionFallback(feedback, "conclusion")
          )
        });
        availableKeys.delete("conclusion");
      }

      Array.from(availableKeys).forEach(key => {
        const section = sections[key] || {};
        nextStream.push({
          title: sectionDisplayTitleMap[key] || formatSectionTitle(key),
          points: toPoints(section),
          content: toSectionContent(section, getLocalSectionFallback(feedback, key))
        });
      });

      return nextStream;
    };

    const hydrateFeedbackView = feedback => {
      activeFeedback.value = feedback;
      variable.value = feedback.great_statement;
      prompt_id.value = feedback.prompt_id;
      feedbackVersion.value = feedback.moduleVersion || feedbackVersion.value;
      feedbackPromptText.value = String(feedback.promptText || "").trim();
      stream.value = buildFeedbackStream(feedback);
    };

    const buildUpdatedFeedbackPayload = feedback => {
      const evidenceEntries = Array.isArray(feedback?.content?.evidence)
        ? feedback.content.evidence
        : [];

      const normalizedEvidencePairs = evidenceEntries.map(entry => {
        const evidenceText = String(entry?.evidence?.userInput || "");
        const analysisText = String(entry?.analysis?.userInput || "");
        return [
          evidenceText,
          analysisText.trim().length ? analysisText : evidenceText
        ];
      });

      return {
        prompt: {
          id: feedback.prompt_id,
          text:
            String(
              feedback.promptText ||
                feedbackPromptText.value ||
                (exersize.value && exersize.value.prompt
                  ? exersize.value.prompt.prompt
                  : "")
            ).trim(),
          moduleVersion: feedback.moduleVersion || feedbackVersion.value
        },
        writing: {
          contextualization:
            feedback?.content?.contextualization?.userInput || "",
          thesis: feedback?.content?.thesis?.userInput || "",
          evidence: normalizedEvidencePairs,
          evidencePairs: normalizedEvidencePairs,
          ...Object.fromEntries(
            normalizedEvidencePairs.flatMap((pair, index) => [
              [`evidence${index + 1}`, pair?.[0] || ""],
              [`analysis${index + 1}`, pair?.[1] || ""]
            ])
          ),
          conclusion: feedback?.content?.conclusion?.userInput || ""
        }
      };
    };

    const closeAttemptConfirmation = () => {
      if (isRefreshingFeedback.value) {
        return;
      }

      showAttemptConfirmation.value = false;
    };

    const openAttemptConfirmation = () => {
      if (updatedFeedbackDisabled.value) {
        return;
      }

      updateFeedbackError.value = "";
      showAttemptConfirmation.value = true;
    };

    const requestUpdatedFeedback = async () => {
      if (!activeFeedback.value || updatedFeedbackDisabled.value) {
        return;
      }

      closeAttemptConfirmation();
      updateFeedbackError.value = "";
      startSubmittingUi();

      try {
        const payload = buildUpdatedFeedbackPayload(activeFeedback.value);
        const result = await getFeedback(payload);

        trackEvent("essay_submitted", {
          feedback_id: feedbackId.value,
          module: activeFeedback.value.moduleVersion || null
        });

        store.dispatch("setBackendFeedback", {
          prompt_id: activeFeedback.value.prompt_id,
          moduleVersion: activeFeedback.value.moduleVersion,
          feedback: result
        });

        await refreshSubscriptionProfile();

        const refreshedFeedback = resolveFeedback();
        if (refreshedFeedback !== 202) {
          hydrateFeedbackView(refreshedFeedback);
        }
      } catch (error) {
        console.error("Failed to generate updated feedback", error);

        if (isAuthRequiredError(error)) {
          updateFeedbackError.value =
            "Please sign in again before requesting updated feedback.";
        } else if (isFeedbackLimitError(error)) {
          await refreshSubscriptionProfile();
          updateFeedbackError.value = "No feedback attempts remaining.";
        } else {
          updateFeedbackError.value =
            "Something went wrong while generating updated feedback. Please try again.";
        }
      } finally {
        stopSubmittingUi();
      }
    };

    //ACCESSING FEEDBACK OBJECT, for future database implementation
    const initialize = async () => {
      const feedback = await resolveFeedback();
      if (feedback === 202) {
        //push to a page not found
      } else {
        console.log("feedback found!", feedback);
        hydrateFeedbackView(feedback);
        console.log(stream.value);
      }
    };

    initialize();
    //CALCULATING SCORE AND FEEDBACK

    //ROUTING
    const handleBack = () => {
      const resolvedPromptId =
        prompt_id.value || activeFeedback.value?.prompt_id || null;
      const routeModule =
        typeof route.query.module === "string" ? route.query.module : null;
      const resolvedModule =
        routeModule ||
        feedbackVersion.value ||
        activeFeedback.value?.moduleVersion ||
        null;

      if (resolvedPromptId && resolvedModule) {
        goToLEQEssay(resolvedPromptId, resolvedModule);
        return;
      }

      if (resolvedModule) {
        goToChoosePrompt(resolvedModule);
        return;
      }

      goToSelectModule();
    };
    return {
      items,
      buttonprops,
      exersize,
      variable,
      score,
      attemptStatusTone,
      remainingAttemptsText,
      handleBack,
      isRefreshingFeedback,
      loadingStepMessage,
      openAttemptConfirmation,
      requestUpdatedFeedback,
      revisionCoachCopy,
      revisionStageLabel,
      showAttemptConfirmation,
      showLongWaitMessage,
      stream,
      updateFeedbackError,
      updatedFeedbackButtonLabel,
      updatedFeedbackDisabled,
      updatedFeedbackHint,
      attemptConfirmationCopy,
      feedbackLoadingMetaText,
      closeAttemptConfirmation,
      goToSelectModule,
      isAuthenticated,
      isProUser,
      remainingFeedbackAttempts,
      // showAttemptDebug and attemptDebugText removed
    };
  }
};
</script>
<style lang="sass" scoped src="@/assets/css/feedback.sass"></style>
<style lang="sass" scoped src="@/assets/css/essayWriting.sass"></style>
<style scoped>
.top-nav-row {
  margin-bottom: 4px;
  align-items: center;
}

.feedback-modules-col {
  margin-left: auto;
}

.top-back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #8c30f5;
  background: transparent;
  font-size: 16px;
  font-weight: 700;
  color: #8c30f5;
  line-height: 1;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, transform 0.15s ease;
}

.top-back-link:hover {
  color: #8c30f5;
  border-color: #8c30f5;
  background: rgba(140, 48, 245, 0.08);
  box-shadow: 0 4px 10px rgba(140, 48, 245, 0.2);
  transform: translateY(-1px);
  text-decoration: none;
}

.top-back-link:active {
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(140, 48, 245, 0.16);
}

.breadcrumb-row {
  margin-bottom: 8px;
}

.page-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 28px;
}

.page-title {
  margin: 0;
  color: #171717;
}

.page-subtitle {
  margin: 12px 0 0;
  max-width: 760px;
  color: #4b5563;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.5;
}

.feedback-summary-grid {
  width: 100%;
  margin-bottom: 28px;
  padding: 0px;
}

.attempt-debug-line {
  margin: 8px 0 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.35;
  word-break: break-word;
}

.revision-actions-card {
  border-radius: 18px;
  padding: 24px;
  background: #fff;
  box-shadow: 0 20px 38px rgba(17, 24, 39, 0.08);
  border: 1px solid #ececf4;
}

.attempt-status-card {
  border-radius: 16px;
  padding: 14px;
  background: #fbfcff;
  box-shadow: none;
  border: 1px solid #e7ebf3;
}

.attempt-status-card--active {
  border-color: #d6e8da;
  background: #f7fcf8;
}

.attempt-status-card--locked {
  border-color: #f3d6c0;
  background: #fffbf5;
}

.attempt-status-card--pro {
  border-color: #d2e8d8;
  background: #f7fcf9;
}

.attempt-status-header {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
}

.feedback-card-label {
  margin: 0 0 8px;
  color: #8c30f5;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.feedback-card-title {
  margin: 0;
  color: #18191f;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.2;
}

.feedback-draft-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(140, 48, 245, 0.1);
  color: #6d28d9;
  font-size: 0.85rem;
  font-weight: 700;
  white-space: nowrap;
}

.revision-actions-copy,
.feedback-system-note,
.attempt-confirm-copy,
.attempt-confirm-meta {
  color: #4b5563;
  font-size: 0.98rem;
  line-height: 1.55;
}

.revision-actions-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feedback-system-note {
  margin: 0;
}

.revision-actions-copy {
  margin: 0;
}

.revision-actions-row {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-top: 6px;
}

.feedback-action-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 240px;
}

.feedback-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  padding: 0 18px;
  border-radius: 14px;
  border: 1px solid transparent;
  font-size: 1rem;
  font-weight: 700;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease,
    color 0.15s ease, border-color 0.15s ease;
}

.feedback-action-button--primary {
  background: linear-gradient(135deg, #8c30f5 0%, #6d28d9 100%);
  color: #fff;
  box-shadow: 0 12px 24px rgba(124, 58, 237, 0.24);
}

.feedback-action-button--primary:hover:enabled {
  transform: translateY(-1px);
}

.feedback-action-button--secondary {
  background: #fff;
  border-color: #d8dbe8;
  color: #18191f;
}

.feedback-action-button--secondary:hover:enabled {
  background: #f9fafb;
}

.feedback-action-button:disabled {
  cursor: not-allowed;
  opacity: 0.58;
  box-shadow: none;
}

.updated-feedback-hint {
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 600;
}

.updated-feedback-hint--disabled {
  color: #b91c1c;
}

.updated-feedback-error {
  color: #b91c1c;
  font-size: 0.95rem;
  font-weight: 600;
}

.attempt-confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.36);
}

.attempt-confirm-card {
  width: 100%;
  max-width: 560px;
  border-radius: 20px;
  background: #fff;
  padding: 28px;
  box-shadow: 0 26px 56px rgba(15, 23, 42, 0.24);
}

.attempt-confirm-title {
  margin: 0 0 12px;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
}

.attempt-confirm-copy {
  margin: 0 0 12px;
}

.attempt-confirm-meta {
  margin: 0 0 20px;
  font-weight: 700;
}

.attempt-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

@media (max-width: 790px) {
  .feedback-summary-grid {
    grid-template-columns: 1fr;
  }

  .page-subtitle {
    font-size: 16px;
  }

  .attempt-status-header {
    flex-direction: column;
  }

  .revision-actions-row {
    flex-direction: column;
  }

  .feedback-action-stack {
    width: 100%;
    min-width: 0;
  }

  .feedback-action-button {
    width: 100%;
  }

  .attempt-confirm-card {
    padding: 22px;
  }
}
</style>
